import { FC, useState } from 'react';
import { Linkedin, Facebook, Instagram, Twitter, CheckCircle2 } from 'lucide-react';

export interface PlatformStatus {
    connected: boolean;
    accountName?: string | null;
    pageName?: string | null;
    username?: string | null;
}

interface PlatformStatusIndicatorProps {
    platform: 'linkedin' | 'facebook' | 'instagram' | 'twitter';
    status: PlatformStatus;
    compact?: boolean;
    onClick?: () => void;
}

const platformConfig = {
    linkedin: {
        name: 'LinkedIn',
        icon: Linkedin,
        color: '#0A66C2',
    },
    facebook: {
        name: 'Facebook',
        icon: Facebook,
        color: '#1877F2',
    },
    instagram: {
        name: 'Instagram',
        icon: Instagram,
        color: '#E4405F',
    },
    twitter: {
        name: 'Twitter',
        icon: Twitter,
        color: '#1DA1F2',
    },
};

export const PlatformStatusIndicator: FC<PlatformStatusIndicatorProps> = ({
    platform,
    status,
    compact = false,
    onClick,
}) => {
    const config = platformConfig[platform];
    const Icon = config.icon;

    const [showTooltip, setShowTooltip] = useState(false);

    const getAccountDisplay = () => {
        if (status.pageName) return status.pageName;
        if (status.username) return `@${status.username}`;
        if (status.accountName) return status.accountName;
        return 'Connected';
    };

    if (compact) {
        return (
            <div
                className="relative"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
            >
                <button
                    onClick={onClick}
                    className={`relative p-2 rounded-md transition-all ${status.connected
                        ? 'bg-green-50 hover:bg-green-100 border border-green-200'
                        : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
                        }`}
                    aria-label={`${config.name} ${status.connected ? 'connected' : 'disconnected'}`}
                >
                    <Icon
                        className="w-4 h-4"
                        style={{ color: status.connected ? config.color : '#D1D5DB' }}
                    />
                    {status.connected && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm" />
                    )}
                </button>

                {showTooltip && (
                    <div className="absolute z-[9999] top-full left-1/2 transform -translate-x-1/2 mt-2 pointer-events-none">
                        <div className="px-2.5 py-1.5 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap">
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-b-gray-900" />
                            <div className="font-medium">{config.name}</div>
                            {status.connected ? (
                                <div className="flex items-center gap-1 text-green-400 text-xs font-medium mt-0.5">
                                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full" />
                                    Connected
                                </div>
                            ) : (
                                <div className="text-gray-400 text-xs mt-0.5">Not connected</div>
                            )}
                            {status.connected && getAccountDisplay() !== 'Connected' && (
                                <div className="text-gray-300 text-[10px] mt-1 border-t border-gray-700 pt-1">
                                    {getAccountDisplay()}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <button
            onClick={onClick}
            className={`relative flex items-center gap-2 p-2 rounded-md border transition-all w-full text-left ${status.connected
                ? 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
        >
            <div
                className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0"
                style={{
                    backgroundColor: status.connected ? `${config.color}15` : '#F9FAFB'
                }}
            >
                <Icon
                    className="w-3.5 h-3.5"
                    style={{ color: status.connected ? config.color : '#9CA3AF' }}
                />
            </div>

            <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-gray-900">{config.name}</div>
                <div className="text-[10px] text-gray-500 truncate">
                    {status.connected ? getAccountDisplay() : 'Not connected'}
                </div>
            </div>

            {status.connected && (
                <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0" />
            )}
        </button>
    );
};
